import React, { useState, useEffect } from 'react';
import { applicationsAPI } from '../../utils/api';
import '../../styles/AdminTable.scss';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [totalApplications, setTotalApplications] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(10);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const getApiUrl = () => {
    const envUrl = process.env.REACT_APP_API_URL;
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname;
      if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
        return 'https://pravarainfotech.in/api';
      }
    }
    return envUrl || 'http://localhost:8000/api';
  };
  const API_BASE = getApiUrl();

  useEffect(() => {
    fetchApplications();
  }, [skip]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationsAPI.getAll(skip, limit);
      // Backend returns { data: [...], total: N, message: "..." }
      const appList = response.data?.data || [];
      const total = response.data?.total || 0;
      setApplications(appList);
      setTotalApplications(total);
    } catch (err) {
      setError('Failed to load applications');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#FFA500',
      reviewed: '#4169E1',
      selected: '#228B22',
      rejected: '#DC143C',
    };
    return colors[status] || '#808080';
  };

  const handleStatusUpdate = async (appId, newStatus) => {
    try {
      setStatusUpdating(true);
      const response = await fetch(`${API_BASE}/applications/${appId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        // Update the selected app with new status
        const updatedApp = { ...selectedApp, status: newStatus };
        setSelectedApp(updatedApp);
        // Refresh applications list
        fetchApplications();
        alert('Status updated successfully!');
      } else {
        setError('Failed to update status');
      }
    } catch (err) {
      setError('Error updating status');
      console.error('Error:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        setLoading(true);
        await applicationsAPI.delete(id);
        if (selectedApp && selectedApp.id === id) {
          setSelectedApp(null);
        }
        await fetchApplications();
        alert('Application deleted successfully!');
      } catch (err) {
        setError('Failed to delete application');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePaginationNext = () => {
    if (skip + limit < totalApplications) {
      setSkip(skip + limit);
    }
  };

  const handlePaginationPrev = () => {
    if (skip > 0) {
      setSkip(Math.max(0, skip - limit));
    }
  };

  return (
    <div className="admin-page">
        <div className="page-header">
          <h1>Job Applications</h1>
          <p>View and manage job applications</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className={`content-wrapper ${selectedApp ? 'has-detail' : ''}`}>
          <div className="table-container">
            {loading ? (
              <p>Loading...</p>
            ) : applications.length === 0 ? (
              <p>No applications found</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Position</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app) => (
                    <tr key={app.id}>
                      <td>{app.name}</td>
                      <td>{app.email}</td>
                      <td>{app.job_title || `Job ID: ${app.job_id}`}</td>
                      <td>
                        <span 
                          className="badge"
                          style={{ backgroundColor: getStatusColor(app.status) }}
                        >
                          {app.status}
                        </span>
                      </td>
                      <td>{new Date(app.created_at).toLocaleDateString()}</td>
                      <td className="actions">
                        <button
                          className="btn-view"
                          onClick={() => setSelectedApp(app)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(app.id)}
                          title="Delete Application"
                          style={{ marginLeft: '8px', cursor: 'pointer', border: 'none', background: 'none', fontSize: '18px' }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selectedApp && (
            <div className="detail-panel">
              <div className="panel-header">
                <h2>Application Details</h2>
                <button 
                  className="btn-close" 
                  onClick={() => setSelectedApp(null)}
                >
                  ✕
                </button>
              </div>
              <div className="panel-content">
                <div className="detail-row">
                  <strong>Name:</strong>
                  <span>{selectedApp.name}</span>
                </div>
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span>{selectedApp.email}</span>
                </div>
                <div className="detail-row">
                  <strong>Phone:</strong>
                  <span>{selectedApp.phone}</span>
                </div>
                <div className="detail-row">
                  <strong>Position:</strong>
                  <span>{selectedApp.job_title || `Job ID: ${selectedApp.job_id}`}</span>
                </div>
                <div className="detail-row">
                  <strong>Cover Letter:</strong>
                  <p>{selectedApp.cover_letter || 'N/A'}</p>
                </div>
                <div className="detail-row">
                  <strong>Resume:</strong>
                  {selectedApp.resume_url && (
                    <a 
                      href={`${API_BASE}/applications/${selectedApp.id}/resume`}
                      download
                      title="Download Resume"
                    >
                      📄 Download Resume
                    </a>
                  )}
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>
                  <div className="status-update-group">
                    <select 
                      value={selectedApp.status}
                      onChange={(e) => {
                        const newStatus = e.target.value;
                        if (newStatus !== selectedApp.status) {
                          handleStatusUpdate(selectedApp.id, newStatus);
                        }
                      }}
                      disabled={statusUpdating}
                      className="status-dropdown"
                    >
                      <option value="pending">🟠 Pending</option>
                      <option value="reviewed">🔵 Reviewed</option>
                      <option value="selected">🟢 Selected</option>
                      <option value="rejected">🔴 Rejected</option>
                    </select>
                    <span 
                      className="badge"
                      style={{ backgroundColor: getStatusColor(selectedApp.status) }}
                    >
                      {selectedApp.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <strong>Submitted:</strong>
                  <span>{new Date(selectedApp.created_at).toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pagination">
          <button 
            disabled={skip === 0} 
            onClick={handlePaginationPrev}
          >
            Previous
          </button>
          <span>Page {Math.floor(skip / limit) + 1} of {Math.ceil(totalApplications / limit)}</span>
          <button 
            disabled={skip + limit >= totalApplications}
            onClick={handlePaginationNext}
          >
            Next
          </button>
        </div>
      </div>
  );
};

export default AdminApplications;
